'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Booking {
  id: string;
  bookingRef: string;
  airline: string;
  flightNumber: string;
  origin: string;
  destination: string;
  departureDate: string;
  returnDate?: string;
  tripType: string;
  cabin: string;
  adults: number;
  grandTotal: number;
  paymentMethod: string;
  bookedAt: string;
  passengerName: string;
  passengerEmail: string;
  status: 'confirmed' | 'upcoming' | 'completed' | 'cancelled';
  bookingStatus: string;
  rescheduleDate?: string;
}

interface PassengerDetail {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  passportNumber: string;
  nationality: string;
  passportExpiry: string;
  email: string;
  phone: string;
}

function formatDate(d: string) {
  if (!d) return '';
  const parts = d.split('-');
  if (parts.length !== 3) return d;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(parts[1]) - 1]} ${parseInt(parts[2])}, ${parts[0]}`;
}

function formatBookedAt(iso: string) {
  if (!iso) return '';
  const d = new Date(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function getFlightStatus(departureDate: string, bookingStatus?: string): Booking['status'] {
  if (bookingStatus === 'cancelled') return 'cancelled';
  if (!departureDate) return 'confirmed';
  const dep = new Date(departureDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  dep.setHours(0, 0, 0, 0);
  if (dep < now) return 'completed';
  return 'upcoming';
}

function StatusBadge({ status }: { status: Booking['status'] }) {
  const styles: Record<string, string> = {
    confirmed: 'text-green-700 bg-green-50 border-green-200',
    upcoming: 'text-sky-700 bg-sky-50 border-sky-200',
    completed: 'text-slate-600 bg-slate-100 border-slate-200',
    cancelled: 'text-red-700 bg-red-50 border-red-200',
  };
  const labels: Record<string, string> = {
    confirmed: '✓ Confirmed',
    upcoming: '✈ Upcoming',
    completed: '✓ Completed',
    cancelled: '✕ Cancelled',
  };
  return (
    <span className={`text-xs font-bold border px-2.5 py-1 rounded-full ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

/* ── Cancel Confirmation Modal ── */
function CancelModal({
  booking,
  onConfirm,
  onClose,
  loading,
}: {
  booking: Booking;
  onConfirm: () => void;
  onClose: () => void;
  loading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">Cancel Booking</h2>
            <p className="text-xs text-muted-foreground">This action cannot be undone</p>
          </div>
        </div>
        <div className="bg-muted/40 rounded-xl p-4 mb-5 space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Booking Ref</span>
            <span className="font-bold text-primary tracking-wider">{booking.bookingRef}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Route</span>
            <span className="font-semibold text-foreground">
              {booking.origin.split('(')[1]?.replace(')', '') || booking.origin.slice(0, 3)}
              {' → '}
              {booking.destination.split('(')[1]?.replace(')', '') || booking.destination.slice(0, 3)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Departure</span>
            <span className="font-semibold text-foreground">{formatDate(booking.departureDate)}</span>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Are you sure you want to cancel this booking? The status will be updated to <span className="font-semibold text-red-600">Cancelled</span>.
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 border border-border text-foreground font-semibold text-sm rounded-xl hover:bg-muted transition-all duration-150 disabled:opacity-50"
          >
            Keep Booking
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2.5 bg-red-600 text-white font-bold text-sm rounded-xl hover:bg-red-700 transition-all duration-150 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Cancelling...
              </>
            ) : (
              'Yes, Cancel Booking'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Reschedule Modal ── */
function RescheduleModal({
  booking,
  onConfirm,
  onClose,
  loading,
}: {
  booking: Booking;
  onConfirm: (newDate: string) => void;
  onClose: () => void;
  loading: boolean;
}) {
  const today = new Date();
  today.setDate(today.getDate() + 1);
  const minDate = today.toISOString().split('T')[0];
  const [newDate, setNewDate] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!newDate) {
      setError('Please select a new departure date.');
      return;
    }
    if (newDate <= (booking.departureDate || '')) {
      setError('New date must be after the current departure date.');
      return;
    }
    setError('');
    onConfirm(newDate);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-sky-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div>
            <h2 className="text-base font-bold text-foreground">Reschedule Flight</h2>
            <p className="text-xs text-muted-foreground">Select a new departure date</p>
          </div>
        </div>
        <div className="bg-muted/40 rounded-xl p-4 mb-4 space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Booking Ref</span>
            <span className="font-bold text-primary tracking-wider">{booking.bookingRef}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Route</span>
            <span className="font-semibold text-foreground">
              {booking.origin.split('(')[1]?.replace(')', '') || booking.origin.slice(0, 3)}
              {' → '}
              {booking.destination.split('(')[1]?.replace(')', '') || booking.destination.slice(0, 3)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Current Departure</span>
            <span className="font-semibold text-foreground">{formatDate(booking.departureDate)}</span>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            New Departure Date
          </label>
          <input
            type="date"
            min={minDate}
            value={newDate}
            onChange={e => { setNewDate(e.target.value); setError(''); }}
            className="w-full px-4 py-2.5 border border-border rounded-xl text-sm text-foreground bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
          {error && <p className="text-xs text-red-600 mt-1.5">{error}</p>}
        </div>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 border border-border text-foreground font-semibold text-sm rounded-xl hover:bg-muted transition-all duration-150 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !newDate}
            className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground font-bold text-sm rounded-xl hover:bg-sky-600 transition-all duration-150 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                Rescheduling...
              </>
            ) : (
              'Confirm Reschedule'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Invoice component rendered inside the expanded panel ── */
function BookingInvoice({ booking, passengers }: { booking: Booking; passengers: PassengerDetail[] }) {
  const baseFare = booking.grandTotal / (booking.paymentMethod === 'crypto' ? 0.9 : 1);
  const discount = booking.paymentMethod === 'crypto' ? baseFare * 0.1 : 0;
  const taxes = booking.grandTotal * 0.12;
  const subtotal = booking.grandTotal - taxes;

  return (
    <div className="mt-6 pt-6 border-t border-border">
      <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-4 flex items-center gap-2">
        <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Booking Invoice
      </h3>

      <div className="bg-gradient-to-br from-sky-50/60 to-white border border-sky-100 rounded-2xl overflow-hidden">
        {/* Invoice header */}
        <div className="bg-primary px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-primary-foreground font-extrabold text-lg tracking-tight">BITVOYAGO</p>
            <p className="text-primary-foreground/70 text-xs">bitvoyago.com · Fly smarter, pay with crypto</p>
          </div>
          <div className="text-right">
            <p className="text-primary-foreground/70 text-xs uppercase tracking-wider">Invoice</p>
            <p className="text-primary-foreground font-bold text-sm font-mono">{booking.bookingRef}</p>
            <p className="text-primary-foreground/70 text-xs">{formatBookedAt(booking.bookedAt)}</p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Passenger info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Passenger</p>
              {passengers.length > 0 ? (
                passengers.map((p, i) => (
                  <p key={i} className="text-sm font-semibold text-foreground">{p.firstName} {p.lastName}</p>
                ))
              ) : (
                <p className="text-sm font-semibold text-foreground">{booking.passengerName}</p>
              )}
              {booking.passengerEmail && (
                <p className="text-xs text-muted-foreground">{booking.passengerEmail}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Status</p>
              <StatusBadge status={booking.status} />
            </div>
          </div>

          {/* Flight details table */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Flight Details</p>
            <div className="border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 border-b border-border">
                    <th className="text-left px-4 py-2.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Description</th>
                    <th className="text-right px-4 py-2.5 text-xs font-bold text-muted-foreground uppercase tracking-wider">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="px-4 py-2.5 text-muted-foreground">Airline &amp; Flight</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-foreground">{booking.airline} {booking.flightNumber}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5 text-muted-foreground">Route</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-foreground">
                      {booking.origin.split('(')[1]?.replace(')', '') || booking.origin.slice(0, 3)}
                      {' → '}
                      {booking.destination.split('(')[1]?.replace(')', '') || booking.destination.slice(0, 3)}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5 text-muted-foreground">Departure Date</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-foreground">
                      {booking.rescheduleDate ? (
                        <span>
                          <span className="line-through text-muted-foreground mr-1">{formatDate(booking.departureDate)}</span>
                          <span className="text-sky-600">{formatDate(booking.rescheduleDate)}</span>
                        </span>
                      ) : formatDate(booking.departureDate)}
                    </td>
                  </tr>
                  {booking.returnDate && (
                    <tr>
                      <td className="px-4 py-2.5 text-muted-foreground">Return Date</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-foreground">{formatDate(booking.returnDate)}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="px-4 py-2.5 text-muted-foreground">Cabin Class</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-foreground">{booking.cabin}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5 text-muted-foreground">Passengers</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-foreground">{booking.adults} Adult{booking.adults > 1 ? 's' : ''}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5 text-muted-foreground">Trip Type</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-foreground capitalize">{booking.tripType.replace('_', ' ')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Fare breakdown */}
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Fare Breakdown</p>
            <div className="border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="px-4 py-2.5 text-muted-foreground">Base Fare</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-foreground">${subtotal.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2.5 text-muted-foreground">Taxes &amp; Fees (12%)</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-foreground">${taxes.toFixed(2)}</td>
                  </tr>
                  {discount > 0 && (
                    <tr>
                      <td className="px-4 py-2.5 text-green-700">Crypto Discount (10%)</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-green-700">−${discount.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr className="bg-primary/5">
                    <td className="px-4 py-3 font-bold text-foreground">Total Paid</td>
                    <td className="px-4 py-3 text-right font-extrabold text-amber-600 text-base">${booking.grandTotal.toFixed(2)} USD</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Payment method */}
          <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-border pt-4">
            <span>Payment Method: <span className="font-semibold text-foreground capitalize">{booking.paymentMethod === 'crypto' ? 'Cryptocurrency' : booking.paymentMethod}</span></span>
            <span>Booking ID: <span className="font-mono font-semibold text-foreground">{booking.id?.slice(0, 8).toUpperCase()}</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BookingHistoryPage() {
  const { user, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRef, setExpandedRef] = useState<string | null>(null);
  const [passengerDetails, setPassengerDetails] = useState<Record<string, PassengerDetail[]>>({});
  const [loadingPassengers, setLoadingPassengers] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'upcoming' | 'completed' | 'cancelled'>('all');

  // Cancel/Reschedule state
  const [cancelModal, setCancelModal] = useState<Booking | null>(null);
  const [rescheduleModal, setRescheduleModal] = useState<Booking | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const fetchBookings = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .order('booked_at', { ascending: false });

      if (error) throw error;

      const mapped: Booking[] = (data || []).map((b: Record<string, unknown>) => ({
        id: b.id as string,
        bookingRef: b.booking_ref as string,
        airline: b.airline as string,
        flightNumber: (b.flight_number as string) || '',
        origin: b.origin as string,
        destination: b.destination as string,
        departureDate: (b.departure_date as string) || '',
        returnDate: (b.return_date as string) || '',
        tripType: (b.trip_type as string) || 'one_way',
        cabin: (b.cabin as string) || 'Economy',
        adults: (b.adults as number) || 1,
        grandTotal: parseFloat(String(b.grand_total || 0)),
        paymentMethod: (b.payment_method as string) || 'crypto',
        bookedAt: (b.booked_at as string) || '',
        passengerName: (b.passenger_name as string) || '',
        passengerEmail: (b.passenger_email as string) || '',
        bookingStatus: (b.booking_status as string) || 'confirmed',
        rescheduleDate: (b.reschedule_date as string) || '',
        status: getFlightStatus(
          (b.departure_date as string) || '',
          (b.booking_status as string) || 'confirmed'
        ),
      }));

      setBookings(mapped);
    } catch {
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!authLoading) {
      fetchBookings();
    }
  }, [authLoading, fetchBookings]);

  const fetchPassengers = async (bookingRef: string) => {
    if (passengerDetails[bookingRef]) return;
    setLoadingPassengers(bookingRef);
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('passengers')
        .select('*')
        .eq('booking_ref', bookingRef);
      if (error) throw error;
      const mapped: PassengerDetail[] = (data || []).map((p: Record<string, unknown>) => ({
        firstName: (p.first_name as string) || '',
        lastName: (p.last_name as string) || '',
        dateOfBirth: (p.date_of_birth as string) || '',
        gender: (p.gender as string) || '',
        passportNumber: (p.passport_number as string) || '',
        nationality: (p.nationality as string) || '',
        passportExpiry: (p.passport_expiry as string) || '',
        email: (p.email as string) || '',
        phone: (p.phone as string) || '',
      }));
      setPassengerDetails(prev => ({ ...prev, [bookingRef]: mapped }));
    } catch {
      setPassengerDetails(prev => ({ ...prev, [bookingRef]: [] }));
    } finally {
      setLoadingPassengers(null);
    }
  };

  const toggleExpand = (bookingRef: string) => {
    if (expandedRef === bookingRef) {
      setExpandedRef(null);
    } else {
      setExpandedRef(bookingRef);
      fetchPassengers(bookingRef);
    }
  };

  /* ── Cancel booking ── */
  const handleCancelConfirm = async () => {
    if (!cancelModal) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('bookings')
        .update({
          booking_status: 'cancelled',
          cancelled_at: new Date().toISOString(),
        })
        .eq('id', cancelModal.id);

      if (error) throw error;

      setBookings(prev =>
        prev.map(b =>
          b.id === cancelModal.id
            ? { ...b, bookingStatus: 'cancelled', status: 'cancelled' }
            : b
        )
      );
      setActionSuccess(`Booking ${cancelModal.bookingRef} has been cancelled.`);
      setCancelModal(null);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to cancel booking. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  /* ── Reschedule booking ── */
  const handleRescheduleConfirm = async (newDate: string) => {
    if (!rescheduleModal) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('bookings')
        .update({
          booking_status: 'rescheduled',
          reschedule_date: newDate,
          rescheduled_at: new Date().toISOString(),
        })
        .eq('id', rescheduleModal.id);

      if (error) throw error;

      setBookings(prev =>
        prev.map(b =>
          b.id === rescheduleModal.id
            ? { ...b, bookingStatus: 'rescheduled', rescheduleDate: newDate, status: 'upcoming' }
            : b
        )
      );
      setActionSuccess(`Booking ${rescheduleModal.bookingRef} has been rescheduled to ${formatDate(newDate)}.`);
      setRescheduleModal(null);
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'Failed to reschedule booking. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = (booking: Booking) => {
    const passengers = passengerDetails[booking.bookingRef] || [];
    const passengerRows = passengers.length > 0
      ? passengers.map(p => `
          <tr><td style="padding:6px 12px;color:#64748b;">Passenger</td><td style="padding:6px 12px;text-align:right;font-weight:600;">${p.firstName} ${p.lastName}</td></tr>
          ${p.passportNumber ? `<tr><td style="padding:6px 12px;color:#64748b;">Passport No.</td><td style="padding:6px 12px;text-align:right;font-weight:600;font-family:monospace;">${p.passportNumber}</td></tr>` : ''}
          ${p.nationality ? `<tr><td style="padding:6px 12px;color:#64748b;">Nationality</td><td style="padding:6px 12px;text-align:right;font-weight:600;">${p.nationality}</td></tr>` : ''}
        `).join('')
      : `<tr><td style="padding:6px 12px;color:#64748b;">Passenger</td><td style="padding:6px 12px;text-align:right;font-weight:600;">${booking.passengerName}</td></tr>`;

    const taxes = booking.grandTotal * 0.12;
    const subtotal = booking.grandTotal - taxes;
    const discount = booking.paymentMethod === 'crypto' ? (booking.grandTotal / 0.9) * 0.1 : 0;
    const discountRow = discount > 0
      ? `<tr style="background:#f0fdf4;"><td style="padding:6px 12px;color:#15803d;">Crypto Discount (10%)</td><td style="padding:6px 12px;text-align:right;font-weight:600;color:#15803d;">−$${discount.toFixed(2)}</td></tr>`
      : '';
    const returnRow = booking.returnDate
      ? `<tr><td style="padding:6px 12px;color:#64748b;">Return Date</td><td style="padding:6px 12px;text-align:right;font-weight:600;">${formatDate(booking.returnDate)}</td></tr>`
      : '';
    const effectiveDeparture = booking.rescheduleDate || booking.departureDate;

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>Booking Receipt – ${booking.bookingRef}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; background: #f8fafc; color: #1e293b; }
    .page { max-width: 680px; margin: 40px auto; background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,0.10); }
    .header { background: #0ea5e9; padding: 28px 32px; display: flex; justify-content: space-between; align-items: flex-start; }
    .header-brand { color: #fff; }
    .header-brand h1 { font-size: 22px; font-weight: 900; letter-spacing: -0.5px; }
    .header-brand p { font-size: 12px; opacity: 0.75; margin-top: 2px; }
    .header-ref { text-align: right; color: #fff; }
    .header-ref .label { font-size: 11px; opacity: 0.7; text-transform: uppercase; letter-spacing: 1px; }
    .header-ref .ref { font-size: 16px; font-weight: 800; font-family: monospace; }
    .header-ref .date { font-size: 12px; opacity: 0.75; margin-top: 2px; }
    .body { padding: 28px 32px; }
    .section-title { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #94a3b8; margin-bottom: 10px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 24px; }
    .info-block p.label { font-size: 11px; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 3px; }
    .info-block p.value { font-size: 14px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; border-radius: 10px; overflow: hidden; border: 1px solid #e2e8f0; margin-bottom: 20px; }
    thead tr { background: #f1f5f9; }
    th { padding: 10px 12px; text-align: left; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; color: #64748b; }
    th:last-child { text-align: right; }
    tbody tr { border-top: 1px solid #e2e8f0; }
    .total-row { background: #f0f9ff; }
    .total-row td { padding: 12px; font-weight: 800; font-size: 15px; }
    .total-row td:last-child { text-align: right; color: #d97706; }
    .footer { background: #f8fafc; border-top: 1px solid #e2e8f0; padding: 16px 32px; display: flex; justify-content: space-between; align-items: center; }
    .footer p { font-size: 11px; color: #94a3b8; }
    .status-badge { display: inline-block; padding: 4px 12px; border-radius: 999px; font-size: 11px; font-weight: 700; border: 1px solid; }
    .status-upcoming { color: #0369a1; background: #e0f2fe; border-color: #bae6fd; }
    .status-completed { color: #475569; background: #f1f5f9; border-color: #cbd5e1; }
    .status-confirmed { color: #15803d; background: #f0fdf4; border-color: #bbf7d0; }
    .status-cancelled { color: #dc2626; background: #fef2f2; border-color: #fecaca; }
    @media print { body { background: #fff; } .page { box-shadow: none; margin: 0; border-radius: 0; } }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <div class="header-brand">
        <h1>BITVOYAGO</h1>
        <p>bitvoyago.com · Fly smarter, pay with crypto</p>
      </div>
      <div class="header-ref">
        <div class="label">Booking Receipt</div>
        <div class="ref">${booking.bookingRef}</div>
        <div class="date">${formatBookedAt(booking.bookedAt)}</div>
      </div>
    </div>
    <div class="body">
      <div class="info-grid">
        <div class="info-block">
          <p class="label">Passenger</p>
          <p class="value">${passengers.length > 0 ? `${passengers[0].firstName} ${passengers[0].lastName}` : booking.passengerName}</p>
          ${booking.passengerEmail ? `<p style="font-size:12px;color:#64748b;margin-top:2px;">${booking.passengerEmail}</p>` : ''}
        </div>
        <div class="info-block" style="text-align:right;">
          <p class="label">Status</p>
          <span class="status-badge status-${booking.status}">${booking.status === 'upcoming' ? '✈ Upcoming' : booking.status === 'completed' ? '✓ Completed' : booking.status === 'cancelled' ? '✕ Cancelled' : '✓ Confirmed'}</span>
        </div>
      </div>

      <p class="section-title">Flight Details</p>
      <table>
        <thead><tr><th>Description</th><th style="text-align:right;">Details</th></tr></thead>
        <tbody>
          <tr><td style="padding:6px 12px;color:#64748b;">Airline &amp; Flight</td><td style="padding:6px 12px;text-align:right;font-weight:600;">${booking.airline} ${booking.flightNumber}</td></tr>
          <tr><td style="padding:6px 12px;color:#64748b;">Route</td><td style="padding:6px 12px;text-align:right;font-weight:600;">${booking.origin} → ${booking.destination}</td></tr>
          <tr><td style="padding:6px 12px;color:#64748b;">Departure</td><td style="padding:6px 12px;text-align:right;font-weight:600;">${formatDate(effectiveDeparture)}</td></tr>
          ${returnRow}
          <tr><td style="padding:6px 12px;color:#64748b;">Cabin Class</td><td style="padding:6px 12px;text-align:right;font-weight:600;">${booking.cabin}</td></tr>
          <tr><td style="padding:6px 12px;color:#64748b;">Trip Type</td><td style="padding:6px 12px;text-align:right;font-weight:600;text-transform:capitalize;">${booking.tripType.replace('_', ' ')}</td></tr>
          ${passengerRows}
        </tbody>
      </table>

      <p class="section-title">Fare Breakdown</p>
      <table>
        <tbody>
          <tr><td style="padding:6px 12px;color:#64748b;">Base Fare</td><td style="padding:6px 12px;text-align:right;font-weight:600;">$${subtotal.toFixed(2)}</td></tr>
          <tr><td style="padding:6px 12px;color:#64748b;">Taxes &amp; Fees (12%)</td><td style="padding:6px 12px;text-align:right;font-weight:600;">$${taxes.toFixed(2)}</td></tr>
          ${discountRow}
          <tr class="total-row"><td style="padding:12px;">Total Paid</td><td style="padding:12px;text-align:right;">$${booking.grandTotal.toFixed(2)} USD</td></tr>
        </tbody>
      </table>

      <p style="font-size:12px;color:#64748b;">Payment Method: <strong style="color:#1e293b;">${booking.paymentMethod === 'crypto' ? 'Cryptocurrency' : booking.paymentMethod}</strong></p>
    </div>
    <div class="footer">
      <p>Thank you for flying with Bitvoyago</p>
      <p>Booking ID: ${booking.id?.slice(0, 8).toUpperCase()}</p>
    </div>
  </div>
  <script>window.onload = function(){ window.print(); }</script>
</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onafterprint = () => {
        URL.revokeObjectURL(url);
      };
    }
  };

  const filteredBookings = bookings.filter(b => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'upcoming') return b.status === 'upcoming' || b.status === 'confirmed';
    if (filterStatus === 'completed') return b.status === 'completed';
    if (filterStatus === 'cancelled') return b.status === 'cancelled';
    return true;
  });

  const upcomingCount = bookings.filter(b => b.status === 'upcoming' || b.status === 'confirmed').length;
  const completedCount = bookings.filter(b => b.status === 'completed').length;
  const cancelledCount = bookings.filter(b => b.status === 'cancelled').length;

  const isUpcoming = (b: Booking) => b.status === 'upcoming' || b.status === 'confirmed';

  return (
    <div className="min-h-screen flex flex-col">
      <Topbar />
      <main className="flex-1 bg-background">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-10">

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <svg className="w-5 h-5 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">My Bookings</h1>
                {user && (
                  <p className="text-sm text-muted-foreground">
                    Signed in as <span className="font-medium text-foreground">{user.email}</span>
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Action feedback */}
          {actionSuccess && (
            <div className="mb-5 flex items-center gap-3 bg-green-50 border border-green-200 text-green-800 rounded-xl px-5 py-3.5">
              <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">{actionSuccess}</span>
              <button onClick={() => setActionSuccess(null)} className="ml-auto text-green-600 hover:text-green-800">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
          {actionError && (
            <div className="mb-5 flex items-center gap-3 bg-red-50 border border-red-200 text-red-800 rounded-xl px-5 py-3.5">
              <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">{actionError}</span>
              <button onClick={() => setActionError(null)} className="ml-auto text-red-600 hover:text-red-800">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Auth Loading */}
          {authLoading && (
            <div className="flex items-center justify-center min-h-64">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
          )}

          {/* Not signed in */}
          {!authLoading && !user && (
            <div className="text-center py-24 bg-card border border-border rounded-2xl">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-foreground mb-2">Sign in to view your bookings</h2>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                Access your full flight history, booking status, and passenger details by signing in to your account.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link
                  href="/login"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-bold text-sm rounded-xl hover:bg-sky-600 transition-all duration-150"
                >
                  Sign In
                </Link>
                <Link
                  href="/register"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-semibold text-sm rounded-xl hover:bg-muted transition-all duration-150"
                >
                  Create Account
                </Link>
              </div>
            </div>
          )}

          {/* Signed in */}
          {!authLoading && user && (
            <>
              {/* Stats row */}
              {!loading && bookings.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-card border border-border rounded-xl px-5 py-4">
                    <p className="text-xs text-muted-foreground mb-1">Total Bookings</p>
                    <p className="text-2xl font-extrabold text-foreground">{bookings.length}</p>
                  </div>
                  <div className="bg-card border border-border rounded-xl px-5 py-4">
                    <p className="text-xs text-muted-foreground mb-1">Upcoming Flights</p>
                    <p className="text-2xl font-extrabold text-sky-600">{upcomingCount}</p>
                  </div>
                  <div className="bg-card border border-border rounded-xl px-5 py-4">
                    <p className="text-xs text-muted-foreground mb-1">Completed Trips</p>
                    <p className="text-2xl font-extrabold text-slate-500">{completedCount}</p>
                  </div>
                  <div className="bg-card border border-border rounded-xl px-5 py-4">
                    <p className="text-xs text-muted-foreground mb-1">Cancelled</p>
                    <p className="text-2xl font-extrabold text-red-500">{cancelledCount}</p>
                  </div>
                </div>
              )}

              {/* Filter tabs */}
              {!loading && bookings.length > 0 && (
                <div className="flex items-center gap-2 mb-5 flex-wrap">
                  {(['all', 'upcoming', 'completed', 'cancelled'] as const).map(f => (
                    <button
                      key={f}
                      onClick={() => setFilterStatus(f)}
                      className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-150 capitalize ${
                        filterStatus === f
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      {f === 'all' ? `All (${bookings.length})` : f === 'upcoming' ? `Upcoming (${upcomingCount})` : f === 'completed' ? `Completed (${completedCount})` : `Cancelled (${cancelledCount})`}
                    </button>
                  ))}
                </div>
              )}

              {/* Loading bookings */}
              {loading && (
                <div className="flex items-center justify-center min-h-64">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
                </div>
              )}

              {/* Empty state */}
              {!loading && bookings.length === 0 && (
                <div className="text-center py-24 bg-card border border-border rounded-2xl">
                  <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  </div>
                  <h2 className="text-lg font-bold text-foreground mb-2">No bookings yet</h2>
                  <p className="text-sm text-muted-foreground mb-6">
                    Bookings made with <span className="font-medium">{user.email}</span> will appear here after confirmation.
                  </p>
                  <Link
                    href="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-bold text-sm rounded-xl hover:bg-sky-600 transition-all duration-150"
                  >
                    Search Flights
                  </Link>
                </div>
              )}

              {/* Booking cards */}
              {!loading && filteredBookings.length > 0 && (
                <div className="space-y-4">
                  {filteredBookings.map((booking, idx) => (
                    <div
                      key={`${booking.bookingRef}-${idx}`}
                      className={`bg-card border rounded-2xl overflow-hidden transition-all duration-200 ${
                        booking.status === 'cancelled' ?'border-red-200 opacity-80' :'border-border hover:border-primary/30'
                      }`}
                    >
                      {/* Card Header */}
                      <div className="px-6 py-4 border-b border-border bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                          </div>
                          <div>
                            <p className="text-sm font-bold text-foreground">{booking.airline} · {booking.flightNumber}</p>
                            <p className="text-xs text-muted-foreground">Booked {formatBookedAt(booking.bookedAt)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <StatusBadge status={booking.status} />
                          {booking.bookingStatus === 'rescheduled' && (
                            <span className="text-xs font-bold text-sky-700 bg-sky-50 border border-sky-200 px-2.5 py-1 rounded-full">
                              ↻ Rescheduled
                            </span>
                          )}
                          {booking.paymentMethod === 'crypto' && (
                            <span className="text-xs font-bold text-amber-700 bg-amber-100 border border-amber-200 px-2.5 py-1 rounded-full flex items-center gap-1">
                              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M11.944 17.97L4.58 13.62 11.943 24l7.37-10.38-7.372 4.35h.003zM12.056 0L4.69 12.223l7.365 4.354 7.365-4.35L12.056 0z"/>
                              </svg>
                              Crypto
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="px-6 py-5">
                        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                          {/* Route */}
                          <div className="flex items-center gap-4 flex-1">
                            <div>
                              <p className="text-2xl font-extrabold text-foreground">
                                {booking.origin.split('(')[1]?.replace(')', '') || booking.origin.slice(0, 3)}
                              </p>
                              <p className="text-xs text-muted-foreground truncate max-w-[120px]">{booking.origin.split('(')[0].trim()}</p>
                            </div>
                            <div className="flex flex-col items-center gap-1 flex-1">
                              <div className="w-full flex items-center gap-2">
                                <div className="flex-1 h-px bg-border" />
                                <svg className="w-4 h-4 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                                <div className="flex-1 h-px bg-border" />
                              </div>
                              <span className="text-xs text-muted-foreground capitalize">{booking.tripType.replace('_', ' ')}</span>
                            </div>
                            <div>
                              <p className="text-2xl font-extrabold text-foreground">
                                {booking.destination.split('(')[1]?.replace(')', '') || booking.destination.slice(0, 3)}
                              </p>
                              <p className="text-xs text-muted-foreground truncate max-w-[120px]">{booking.destination.split('(')[0].trim()}</p>
                            </div>
                          </div>

                          {/* Details */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-6">
                            <div>
                              <p className="text-xs text-muted-foreground">Departure</p>
                              {booking.rescheduleDate ? (
                                <div>
                                  <p className="text-xs line-through text-muted-foreground">{formatDate(booking.departureDate)}</p>
                                  <p className="text-sm font-semibold text-sky-600">{formatDate(booking.rescheduleDate)}</p>
                                </div>
                              ) : (
                                <p className="text-sm font-semibold text-foreground">{formatDate(booking.departureDate)}</p>
                              )}
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Cabin</p>
                              <p className="text-sm font-semibold text-foreground">{booking.cabin}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Ref</p>
                              <p className="text-sm font-bold text-primary tracking-wider">{booking.bookingRef}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Total Paid</p>
                              <p className="text-sm font-extrabold text-amber-600">${booking.grandTotal.toLocaleString()}</p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
                            <button
                              onClick={() => toggleExpand(booking.bookingRef)}
                              className="flex items-center gap-1.5 px-3 py-2 border border-primary/30 bg-primary/5 text-primary text-xs font-semibold rounded-lg hover:bg-primary/10 transition-all duration-150"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              {expandedRef === booking.bookingRef ? 'Hide' : 'Details'}
                            </button>
                            <button
                              onClick={() => handleDownloadPDF(booking)}
                              className="flex items-center gap-1.5 px-3 py-2 border border-red-200 bg-red-50 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-100 transition-all duration-150"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                              </svg>
                              PDF Receipt
                            </button>
                            {/* Cancel & Reschedule — only for upcoming bookings */}
                            {isUpcoming(booking) && (
                              <>
                                <button
                                  onClick={() => { setActionError(null); setRescheduleModal(booking); }}
                                  className="flex items-center gap-1.5 px-3 py-2 border border-sky-200 bg-sky-50 text-sky-700 text-xs font-semibold rounded-lg hover:bg-sky-100 transition-all duration-150"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  Reschedule
                                </button>
                                <button
                                  onClick={() => { setActionError(null); setCancelModal(booking); }}
                                  className="flex items-center gap-1.5 px-3 py-2 border border-red-300 bg-red-50 text-red-700 text-xs font-semibold rounded-lg hover:bg-red-100 transition-all duration-150"
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                  Cancel
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Expanded Details Panel */}
                        {expandedRef === booking.bookingRef && (
                          <div className="mt-5 pt-5 border-t border-border">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Booking Details */}
                              <div>
                                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Booking Details</h3>
                                <div className="space-y-2.5">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Booking Reference</span>
                                    <span className="font-bold text-primary tracking-wider">{booking.bookingRef}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Flight</span>
                                    <span className="font-semibold text-foreground">{booking.airline} {booking.flightNumber}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Route</span>
                                    <span className="font-semibold text-foreground">{booking.origin} → {booking.destination}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Departure</span>
                                    <span className="font-semibold text-foreground">
                                      {booking.rescheduleDate ? (
                                        <span>
                                          <span className="line-through text-muted-foreground mr-1">{formatDate(booking.departureDate)}</span>
                                          <span className="text-sky-600">{formatDate(booking.rescheduleDate)}</span>
                                        </span>
                                      ) : formatDate(booking.departureDate)}
                                    </span>
                                  </div>
                                  {booking.returnDate && (
                                    <div className="flex justify-between text-sm">
                                      <span className="text-muted-foreground">Return</span>
                                      <span className="font-semibold text-foreground">{formatDate(booking.returnDate)}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Cabin Class</span>
                                    <span className="font-semibold text-foreground">{booking.cabin}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Passengers</span>
                                    <span className="font-semibold text-foreground">{booking.adults} adult{booking.adults > 1 ? 's' : ''}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Trip Type</span>
                                    <span className="font-semibold text-foreground capitalize">{booking.tripType.replace('_', ' ')}</span>
                                  </div>
                                  <div className="flex justify-between text-sm pt-2 border-t border-border">
                                    <span className="text-muted-foreground font-semibold">Total Paid</span>
                                    <span className="font-extrabold text-amber-600">${booking.grandTotal.toLocaleString()} USD</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Payment Method</span>
                                    <span className="font-semibold text-foreground capitalize">
                                      {booking.paymentMethod === 'crypto' ? 'Cryptocurrency' : booking.paymentMethod}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Passenger Details */}
                              <div>
                                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Passenger Details</h3>
                                {loadingPassengers === booking.bookingRef ? (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                                    Loading passenger info...
                                  </div>
                                ) : passengerDetails[booking.bookingRef]?.length ? (
                                  <div className="space-y-4">
                                    {passengerDetails[booking.bookingRef].map((p, pi) => (
                                      <div key={pi} className="bg-muted/30 rounded-xl p-4 space-y-2">
                                        <p className="text-sm font-bold text-foreground">{p.firstName} {p.lastName}</p>
                                        {p.gender && (
                                          <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Gender</span>
                                            <span className="font-medium text-foreground capitalize">{p.gender}</span>
                                          </div>
                                        )}
                                        {p.dateOfBirth && (
                                          <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Date of Birth</span>
                                            <span className="font-medium text-foreground">{formatDate(p.dateOfBirth)}</span>
                                          </div>
                                        )}
                                        {p.nationality && (
                                          <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Nationality</span>
                                            <span className="font-medium text-foreground">{p.nationality}</span>
                                          </div>
                                        )}
                                        {p.passportNumber && (
                                          <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Passport No.</span>
                                            <span className="font-medium text-foreground font-mono">{p.passportNumber}</span>
                                          </div>
                                        )}
                                        {p.passportExpiry && (
                                          <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Passport Expiry</span>
                                            <span className="font-medium text-foreground">{formatDate(p.passportExpiry)}</span>
                                          </div>
                                        )}
                                        {p.email && (
                                          <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Email</span>
                                            <span className="font-medium text-foreground">{p.email}</span>
                                          </div>
                                        )}
                                        {p.phone && (
                                          <div className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">Phone</span>
                                            <span className="font-medium text-foreground">{p.phone}</span>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="bg-muted/30 rounded-xl p-4 space-y-2">
                                    <p className="text-sm font-bold text-foreground">{booking.passengerName}</p>
                                    {booking.passengerEmail && (
                                      <p className="text-xs text-muted-foreground">{booking.passengerEmail}</p>
                                    )}
                                    <p className="text-xs text-muted-foreground mt-2">Detailed passenger records not available for this booking.</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Formatted Invoice Section */}
                            {loadingPassengers !== booking.bookingRef && (
                              <BookingInvoice
                                booking={booking}
                                passengers={passengerDetails[booking.bookingRef] || []}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* No results for filter */}
              {!loading && bookings.length > 0 && filteredBookings.length === 0 && (
                <div className="text-center py-16 bg-card border border-border rounded-2xl">
                  <p className="text-muted-foreground text-sm">No {filterStatus} bookings found.</p>
                  <button onClick={() => setFilterStatus('all')} className="mt-3 text-sm text-primary font-semibold hover:underline">
                    Show all bookings
                  </button>
                </div>
              )}

              {/* Back to search */}
              {!loading && (
                <div className="mt-8 text-center">
                  <Link
                    href="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground font-bold text-sm rounded-xl hover:bg-sky-600 transition-all duration-150"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                    </svg>
                    Search New Flight
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />

      {/* Cancel Modal */}
      {cancelModal && (
        <CancelModal
          booking={cancelModal}
          onConfirm={handleCancelConfirm}
          onClose={() => setCancelModal(null)}
          loading={actionLoading}
        />
      )}

      {/* Reschedule Modal */}
      {rescheduleModal && (
        <RescheduleModal
          booking={rescheduleModal}
          onConfirm={handleRescheduleConfirm}
          onClose={() => setRescheduleModal(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
}
